/**
 * Every word that can appear in `wallet.transactions.type`, as ONE definition.
 *
 * Nothing on any write path enforces this list: `POST /wallet/internal/tx` takes
 * `t.String({ minLength: 1, maxLength: 32 })` and `PostTxParams.type` is a bare
 * `string`, so a service that invents a word gets its money moved and its row
 * written. What the list is for is everything downstream — the shadow journal's
 * account map, the category filter, and the back office's label and filter
 * catalogue, each of which silently mistreats a word it does not know. A row the
 * back office cannot name is also a row it cannot filter to, which is how six
 * kinds of movement became invisible on a page whose whole purpose is showing
 * them.
 *
 * No TypeBox and no imports on purpose, the same rule `approval-limits.ts`
 * states: a BROWSER bundle reads this, and a value import that dragged a
 * validation library in would be paid for on a screen that validates nothing.
 * `domain/wallet.ts` builds the TypeBox union from these arrays; importers who
 * want the words take this subpath.
 *
 * Adding a word here is not enough on its own. A new type needs, in the same
 * change: a prefix it matches in `CATEGORY_TYPE_PREFIXES`
 * (`apps/wallet/src/repo/ledger.ts`), an account pair in `buildWalletPostings`
 * (`apps/wallet/src/repo/wallet-postings.ts`) or the journal files it as
 * `unmapped_type`, and a `wallet.txTypes.<type>` label in all seven back-office
 * catalogues. `apps/wallet/src/db/transaction-types.test.ts` holds the first and
 * the last of those.
 */

/**
 * The types current code writes. Each has a live writer, named in
 * `apps/wallet/src/db/transaction-types.test.ts`, which fails if that writer
 * moves or stops writing it.
 */
export const TRANSACTION_TYPES = [
  // Cashier — svc-finance, on an approved request.
  'deposit',
  'withdraw',
  'withdraw_reject_refund',

  // Bets booked by svc-bet, which owns the slip.
  'bet_place',
  'bet_payout',
  'bet_cancel',
  'payout_cancel',

  // Seamless play. The debit is one word; the credit is `seamless_<category>`,
  // so the credit words are the categories a provider may settle in, one of
  // which (`refund`, a stake handed back in part) is the refund's own word.
  'seamless_bet',
  'seamless_win',
  'seamless_jackpot',
  'seamless_promo',
  'seamless_bonus',
  'seamless_freegame',
  'seamless_refund',
  // The async mirror of a seamless settle, written by svc-wallet's own consumer
  // rather than by the provider's synchronous call.
  'bet_settle_win',

  // Promotions and bonuses.
  'promo_credit',
  'promo_release',
  'promo_bonus',
  'bonus_activate',
  'bonus_forfeit',
  // A bonus conversion is one pool transfer, so it books TWO rows under two
  // words — the debit leg and the credit leg are not the same movement.
  'bonus_convert_debit',
  'bonus_convert_credit',

  // Cashback and turnover rebate share one word; `ref_kind` is what separates
  // them, and `apps/admin/src/reconcile/engine.ts` matches on that.
  'cashback',
  'cashback_convert',

  // Commission, and the player-initiated move of it into spendable cash.
  'commission',
  'commission_transfer',
  'referral_commission',

  // The two directions of a manual back-office adjustment.
  'admin_add',
  'admin_sub',
] as const;

export type WrittenTransactionType = (typeof TRANSACTION_TYPES)[number];

/**
 * Words with no writer left in the tree.
 *
 * They stay in the vocabulary because the column keeps what earlier code wrote:
 * dropping one would leave an old row with no label and no filter that reaches
 * it, which is the bug this module exists to close, aimed backwards. They are
 * separated from the list above so that "which movements does the platform make
 * today" stays an answerable question.
 *
 * `daily_checkin` is the odd one: it is a promotion KIND, and claiming one
 * credits as `promo_credit`, but `CATEGORY_TYPE_PREFIXES` and the journal's
 * account map both still name it as a ledger type.
 */
export const LEGACY_TRANSACTION_TYPES = [
  'deposit_rollback',
  'promo_expire',
  'bonus',
  'referral',
  'coupon',
  'reward_credit',
  'daily_checkin',
  'adjust',
  'rollback',
] as const;

export type LegacyTransactionType = (typeof LEGACY_TRANSACTION_TYPES)[number];

/** Every base word, current or historical. */
export const BASE_TRANSACTION_TYPES = [
  ...TRANSACTION_TYPES,
  ...LEGACY_TRANSACTION_TYPES,
] as const;

export type BaseTransactionType = WrittenTransactionType | LegacyTransactionType;

/**
 * What `compensateTransaction` appends to the reversed row's own type.
 *
 * Compensation is open over the whole vocabulary — the reverse row is typed
 * `` `${original.type}_compensate` `` from whatever it found — so the words it
 * can produce are not a list somebody maintains, they are this suffix applied to
 * every base word.
 */
export const COMPENSATION_SUFFIX = '_compensate';

export type CompensationTransactionType = `${BaseTransactionType}${typeof COMPENSATION_SUFFIX}`;

export const COMPENSATION_TRANSACTION_TYPES = BASE_TRANSACTION_TYPES.map(
  (type) => `${type}${COMPENSATION_SUFFIX}` as CompensationTransactionType,
);

/** Every word a ledger row can carry. */
export const LEDGER_TRANSACTION_TYPES: readonly LedgerTransactionType[] = [
  ...BASE_TRANSACTION_TYPES,
  ...COMPENSATION_TRANSACTION_TYPES,
];

export type LedgerTransactionType = BaseTransactionType | CompensationTransactionType;

/**
 * The base word a compensation reverses, or `null` when `type` is not one.
 *
 * A caller that only needs to know "is this a reversal" reads the null-ness; one
 * that needs to say WHAT was reversed gets the word back and can label it.
 */
export function compensatedBaseType(type: string): BaseTransactionType | null {
  if (!type.endsWith(COMPENSATION_SUFFIX)) return null;
  const base = type.slice(0, -COMPENSATION_SUFFIX.length);
  return (BASE_TRANSACTION_TYPES as readonly string[]).includes(base)
    ? (base as BaseTransactionType)
    : null;
}
